import { Moment } from 'moment'

import { ConfigFilterItem, ConfigItem } from 'Core/interfaces'

export enum BalesFields {
	id = 'id',
	pounds = 'pounds',
	isActive = 'isActive',
	baleDate = 'baleDate',
	kilograms = 'kilograms',
	baleNumber = 'baleNumber',
	scenarioCode = 'scenarioCode',
	materialName = 'materialName',
	item = 'item',
	folioNumber = 'folioNumber',
	serviceSite = 'serviceSite',
	partialNumber = 'partialNumber',
	outboundTrailer = 'outboundTrailer',
	inboundTrailer = 'inboundTrailer',
	processingCenter = 'processingCenter',
	inboundSealNumber = 'inboundSealNumber',
	serviceOccurrenceId = 'serviceOccurrenceId',
	processingCenterLocation = 'processingCenterLocation',
	comment = 'comment'
}

export enum BaleFormFields {
	processingCenterId = 'processingCenterId',
	processingCenterLocationId = 'processingCenterLocationId',
	baleDate = 'baleDate',
	baleNumber = 'baleNumber',
	folioNumber = 'folioNumber',
	partialNumber = 'partialNumber',
	inboundTrailerId = 'inboundTrailerId',
	productTypeId = 'productTypeId',
	kilograms = 'kilograms',
	pounds = 'pounds',
	isActive = 'isActive',
	comment = 'comment',
	scenarioCode = 'scenarioCode'
}

export enum BalesSearchFields {
	baleNumbers = 'baleNumbers',
	folioNumbers = 'folioNumbers',
	inboundSealNumbers = 'inboundSealNumbers',
	outboundSealNumbers = 'outboundSealNumbers',
	serviceSiteIds = 'serviceSiteIds',
	serviceSiteNames = 'serviceSiteNames',
	itemIds = 'itemIds',
	itemNames = 'itemNames'
}

export enum BalesFilterFields {
	processingCenterId = 'processingCenterId',
	baleDate = 'baleDate',
	processingCenterLocationId = 'processingCenterLocationId',
	productTypeId = 'productTypeId',
	includeInactive = 'includeInactive',
	departureDate = 'departureDate',
	showShippedBales = 'showShippedBales'
}

export enum ShippedBalesFields {
	outboundSealNumber = 'outboundSealNumber',
	departureDate = 'departureDate'
}

export declare namespace Bale {
	type MaterialName = NullableEntity & {
		productType: NullableEntity
	}
	interface Basic {
		[BalesFields.id]: number
		[BalesFields.processingCenter]: NullableEntity
		[BalesFields.processingCenterLocation]: NullableEntity
		[BalesFields.baleNumber]: BasicString
		[BalesFields.folioNumber]: string
		[BalesFields.partialNumber]: BasicString
		[BalesFields.scenarioCode]: BasicString
		[BalesFields.inboundSealNumber]: BasicString
		[BalesFields.baleDate]: string
		[BalesFields.serviceSite]: Entity
		[BalesFields.pounds]: number
		[BalesFields.kilograms]: number
		[BalesFields.materialName]: MaterialName
		[BalesFields.item]: NullableEntity
		[ShippedBalesFields.departureDate]: BasicString
		[ShippedBalesFields.outboundSealNumber]: BasicString
		[BalesFields.isActive]: boolean
		[BalesFields.comment]: BasicString
	}

	interface Detailed extends Basic {
		[BalesFields.inboundTrailer]: IdCode
		[BalesFields.outboundTrailer]: IdCode | null
		[BalesFields.serviceOccurrenceId]: number
	}

	namespace Form {
		interface Generic {
			[BaleFormFields.processingCenterId]: number | null
			[BaleFormFields.processingCenterLocationId]: LabelValue<number> | null
			[BaleFormFields.baleDate]: Moment | null
			[BaleFormFields.baleNumber]: BasicString
			[BaleFormFields.folioNumber]: BasicString
			[BaleFormFields.partialNumber]: BasicString
			[BaleFormFields.inboundTrailerId]: LabelValue<number> | null
			[BaleFormFields.productTypeId]: LabelValue | null
			[BaleFormFields.kilograms]: number | null
			[BaleFormFields.pounds]: number | null
			[BaleFormFields.isActive]: boolean
			[BaleFormFields.comment]: BasicString
			[BaleFormFields.scenarioCode]: BasicString
		}
	}

	namespace Request {
		interface Generic {
			[BaleFormFields.processingCenterId]: number | null
			[BaleFormFields.processingCenterLocationId]: number | null
			[BaleFormFields.baleDate]: BasicString
			[BaleFormFields.baleNumber]: BasicString
			[BaleFormFields.folioNumber]: BasicString
			[BaleFormFields.partialNumber]: BasicString
			[BaleFormFields.inboundTrailerId]: number | null
			[BaleFormFields.productTypeId]: BasicString
			[BaleFormFields.kilograms]: number | null
			[BaleFormFields.isActive]?: boolean
			[BaleFormFields.comment]: BasicString
			[BaleFormFields.scenarioCode]: BasicString
		}
	}

	namespace Search {
		interface Result {
			[BalesSearchFields.baleNumbers]: number[]
			[BalesSearchFields.folioNumbers]: number[]
			[BalesSearchFields.inboundSealNumbers]: number[]
			[BalesSearchFields.outboundSealNumbers]: number[]
			[BalesSearchFields.serviceSiteIds]: Entity[]
			[BalesSearchFields.serviceSiteNames]: Entity[]
			[BalesSearchFields.itemIds]: Entity[]
			[BalesSearchFields.itemNames]: Entity[]
		}

		interface Config {
			[BalesSearchFields.baleNumbers]: ConfigItem<BalesSearchFields>
			[BalesSearchFields.folioNumbers]: ConfigItem<BalesSearchFields>
			[BalesSearchFields.inboundSealNumbers]: ConfigItem<BalesSearchFields>
			[BalesSearchFields.outboundSealNumbers]: ConfigItem<BalesSearchFields>
			[BalesSearchFields.serviceSiteIds]: ConfigItem<BalesSearchFields>
			[BalesSearchFields.serviceSiteNames]: ConfigItem<BalesSearchFields>
			[BalesSearchFields.itemIds]: ConfigItem<BalesSearchFields>
			[BalesSearchFields.itemNames]: ConfigItem<BalesSearchFields>
		}
	}

	namespace Filter {
		interface Config {
			[BalesFilterFields.processingCenterId]: ConfigFilterItem<string, string>
			[BalesFilterFields.baleDate]: ConfigFilterItem<string, null>
			[BalesFilterFields.processingCenterLocationId]: ConfigFilterItem<string, string>
			[BalesFilterFields.productTypeId]: ConfigFilterItem<string, string>
			[BalesFilterFields.includeInactive]: ConfigFilterItem<string, boolean>
			[BalesFilterFields.departureDate]: ConfigFilterItem<string, null>
			[BalesFilterFields.showShippedBales]: ConfigFilterItem<string, boolean>
		}
	}
}
